import { ActionContext } from 'vuex';
import { ISearchCommandOptions } from '@llws/dynamic-router-utils';
import {
  IAmenities,
  IBuildings,
  IBuildings2Amenities,
  IBuildings2HousingTypes,
  IBuildings2OwnershipTypes,
  IBuildings2Utilities,
  IBuildingsHasPhotos,
  IBuildingUtilities,
  IHousingTypes,
  IListingFloorplans,
  IListingPhotos,
  IOwnershipGroupHasBuildings,
  IOwnershipGroups,
  IOwnershipTypes,
  ISuitesHasPhotos,
  ISuiteTypes,
  ISuiteTypesFloorplans,
  IVideos,
  IVirtualTours,
} from '@llws/lift-entity-interfaces';
import dayjs from 'dayjs';
import vm from '@/main';
import properties from '@/shared/services/properties';
import {
  IApiResponse,
  IExportFilterPayload,
  IGenericObject,
  IJunctionPostMetaResponse,
  IMetaResponse,
  IModifyPayload,
  subsetObject,
} from '@/shared';
import {
  IBuildingToEdit,
  IPropertyAssignedPhotoPayload,
  IPropertyChildInsertPayload,
  IPropertyChildModifyPayload,
  IPropertyInsertUploadResultPayload,
  IPropertySettings,
  IPropertyStoreState,
  IPropertyUploadResultPayload,
} from '@/shared/interfaces/property-store';
import { IActionPayload } from '@/shared/interfaces/store-base';
import { adminRequest } from '@/shared/request-configs/admin-request';
import store from '..';
import { downloadCSV } from '@/shared/helpers/download-csv';
import { IFilterPayloadV2 } from '@/shared/interfaces/filter-payload-v2';
import { hasSFUnitRestriction } from '@/shared/helpers/building-type-options';

const baseState: IPropertyStoreState = {
  list: undefined,
  listTimestamp: 0,
  single: undefined,
  singleTimestamp: 0,
  unitModels: undefined,
  units: undefined,
  photos: undefined,
  settings: undefined,
  floorplans: undefined,
  homepageList: undefined,
  currentSearchFilters: { alias: 'building' },
  exportFields: [
    {
      display: 'Client ID',
      property: 'clientId',
    },
    {
      display: 'Property Name',
      property: 'buildingName',
    },
    {
      display: 'Property ID',
      property: 'id',
    },
    {
      display: 'Street Number',
      property: 'streetNumber',
    },
    {
      display: 'Street Name',
      property: 'streetName',
    },
    {
      display: 'City',
      property: 'city.cityName',
      csvCustomTemplate: '{city.cityName}',
    },
    {
      display: 'Province',
      property: 'city.province.provinceName',
      csvCustomTemplate: '{city.province.provinceName}',
    },
    {
      display: 'Postal Code',
      property: 'postal',
    },
    {
      display: 'Property Type',
      property: 'buildingType.name',
    },
    {
      display: 'Status',
      property: 'status',
    },
    {
      display: 'Property Timezone',
      property: 'timezone',
    },
  ],

  cleanProperty: undefined,
};

export default {
  namespaced: true, // 'properties/'

  state: baseState,

  getters: {
    list(state: IPropertyStoreState): IBuildings[] {
      return [...state.list?.data || []];
    },

    quickUpdateContactInfoList(state: IPropertyStoreState): IBuildings[] {
      return [...state.list?.data?.filter((property) => vm()?.$rsGlobals.$userHasAccess('buildings', 'update', property.id ?? 0)) || []];
    },

    quickUpdateList(state: IPropertyStoreState): IBuildings[] {
      const quickUpdateProperties = [...state.list?.data?.filter((property) => vm()?.$rsGlobals.$userHasAccess('buildings', 'update', property.id ?? 0)) || []];
      return quickUpdateProperties.map((p) => {
        const suiteTypes = p.suiteTypes ?? [];
        return {
          ...p,
          suiteTypes: suiteTypes.map((st) => ({
            ...st,
            available: (
              st.available === 1 && st.availabilityDate && dayjs(st.availabilityDate).isAfter(dayjs(), 'day')
                ? 2
                : st.available
            ),
          })),
        };
      });
    },

    single(state: IPropertyStoreState): IBuildingToEdit | undefined {
      if (state.single?.data) {
        return { ...state.single.data };
      }
      return undefined;
    },

    unitModels(state: IPropertyStoreState): ISuiteTypes[] {
      return [...state.unitModels?.data || []];
    },

    units(state: IPropertyStoreState): ISuiteTypes[] {
      return [...state.units?.data || []];
    },

    assignedPhotos(state: IPropertyStoreState): IListingPhotos[] {
      return [...state.photos?.data?.assigned || []].filter((v) => v.active === 1);
    },

    availablePhotos(state: IPropertyStoreState): IListingPhotos[] {
      return [...state.photos?.data?.available || []];
    },

    floorplans(state: IPropertyStoreState) {
      return [...state.floorplans?.data || []];
    },

    settings(state: IPropertyStoreState) {
      return state.settings?.data ? state.settings.data : undefined;
    },

    listMeta(state: IPropertyStoreState) {
      return state.list?.meta;
    },

    unitMeta(state: IPropertyStoreState) {
      return state.units?.meta;
    },

    unitModelMeta(state: IPropertyStoreState) {
      return state.unitModels?.meta;
    },

    exportFields(state: IPropertyStoreState) {
      return state.exportFields;
    },

    currentSearchFilters(state: IPropertyStoreState) {
      return state.currentSearchFilters;
    },

    homepageList(state: IPropertyStoreState): IBuildings[] {
      return [...state.homepageList?.data || []];
    },

    cleanProperty(state: IPropertyStoreState) {
      return state.cleanProperty;
    },
  },

  mutations: {
    set(state: IPropertyStoreState, payload: IApiResponse<IBuildings | IBuildings[], IMetaResponse> | IBuildings) {
      if ('data' in payload && Array.isArray(payload.data)) {
        state.list = payload as IApiResponse<IBuildings[], IMetaResponse>;
        return;
      }

      state.single = 'id' in payload
        ? { data: payload as IBuildingToEdit, meta: null }
        : payload as IApiResponse<IBuildingToEdit, IMetaResponse>;
    },

    setUnits(state: IPropertyStoreState, payload: IApiResponse<ISuiteTypes[], IMetaResponse>) {
      state.units = payload;
      if (state.single?.data) {
        state.single.data.suiteTypes = payload.data;
      }
    },

    setPhotos(state: IPropertyStoreState, payload: Record<string, IListingPhotos[]>) {
      state.photos = payload;
    },

    setAvailablePhotos(state: IPropertyStoreState, payload: IListingPhotos[]) {
      if (state.photos?.data) {
        state.photos.data.available = payload;
      }
    },

    setAssignedPhotos(state: IPropertyStoreState, payload: IListingPhotos[]) {
      if (state.photos?.data) {
        state.photos.data.assigned = payload;
      }
    },

    setUnitModels(state: IPropertyStoreState, payload: IApiResponse<ISuiteTypes[], IMetaResponse>) {
      state.unitModels = payload;
    },

    setSettings(state: IPropertyStoreState, payload: IApiResponse<IPropertySettings, IMetaResponse>) {
      state.settings = payload;
    },

    setFloorplans(
      state: IPropertyStoreState,
      payload: IApiResponse<IListingFloorplans[], IMetaResponse> | IListingFloorplans[],
    ) {
      if (Array.isArray(payload)) {
        state.floorplans = {
          ...state.floorplans,
          ...{ data: payload as IListingFloorplans[] },
        } as IApiResponse<IListingFloorplans[], IMetaResponse>;
      } else {
        state.floorplans = payload;
      }
    },

    reset(state: IPropertyStoreState) {
      state.list = undefined;
      state.single = undefined;
    },

    resetPhotos(state: IPropertyStoreState) {
      state.photos = undefined;
    },

    resetUnitModels(state: IPropertyStoreState) {
      state.unitModels = undefined;
    },

    setCurrentSearchFilters(state: IPropertyStoreState, payload: IPropertyStoreState['currentSearchFilters']) {
      state.currentSearchFilters = payload;
    },

    setHomepageList(state: IPropertyStoreState, payload: IApiResponse<IBuildings[], IMetaResponse>) {
      state.homepageList = payload;
    },

    setCleanProperty(state: IPropertyStoreState, payload: IBuildingToEdit) {
      state.cleanProperty = payload;
    },
  },
  actions: {
    async get(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: { id?: number, [key: string]: unknown },
    ) {
      const result = payload?.id
        ? await properties.getByIdV2(payload.id)
        : await properties.get(payload);
      ctx.commit('set', result);
    },

    async getV2(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IFilterPayloadV2,
    ): Promise<{data?: IBuildings[], meta?: IMetaResponse}> {
      const result = payload?.id
        ? await properties.getByIdV2(payload.id)
        : await properties.getV2(payload.filters);

      ctx.commit('set', result);
      return { data: result?.data!, meta: result.meta };
    },

    async search(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: ISearchCommandOptions,
    ): Promise<IApiResponse<Partial<IBuildings>, IMetaResponse>> {
      const result: IApiResponse<Partial<IBuildings>, IMetaResponse> = await adminRequest.post('/v1/property-api/properties/_search', payload);
      ctx.commit('set', result);
      return result;
    },

    async searchHome(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: ISearchCommandOptions,
    ): Promise<IApiResponse<Partial<IBuildings>, IMetaResponse>> {
      const result: IApiResponse<Partial<IBuildings>, IMetaResponse> = await adminRequest.post('/v1/property-api/properties/_search', payload);
      ctx.commit('setHomepageList', result);
      return result;
    },

    async getUnitModels(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      { propertyId, filters }: { propertyId: number, filters: Record<string, unknown> },
    ) {
      const result = await properties.getUnitModels(propertyId, filters);
      ctx.commit('setUnitModels', result);
    },

    async getUnits(ctx: ActionContext<IPropertyStoreState, typeof store.state>, propertyId: number) {
      const result = await properties.getUnits(propertyId);
      result.data = result.data.map((v: ISuiteTypes) => ({
        ...v,
        ...{
          suitesHasPhotos: (v.suitesHasPhotos || [])
            .filter((p: ISuitesHasPhotos) => p.listingPhoto?.active === 1)
            .sort((a: ISuitesHasPhotos, b: ISuitesHasPhotos) => (a.orderBy > b.orderBy ? 1 : -1)),

          suiteTypesFloorplans: (v.suiteTypesFloorplans || [])
            .sort((a: ISuiteTypesFloorplans, b: ISuiteTypesFloorplans) => (a.orderBy > b.orderBy ? 1 : -1)),
        },
      }));
      ctx.commit('setUnits', result);
    },

    async getSettings(ctx: ActionContext<IPropertyStoreState, typeof store.state>) {
      if (!ctx.state.settings) {
        const result = await properties.getSettingsV2();
        ctx.commit('setSettings', result);
      }
    },

    async getPhotos(ctx: ActionContext<IPropertyStoreState, typeof store.state>, propertyId: number | string) {
      const result = await properties.getPhotos(propertyId);
      ctx.commit('setPhotos', result);
    },

    async save(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IActionPayload<IBuildings>,
    ) {
      const result = await properties.save(payload.data);
      const current = ctx.getters.single;
      const formattedResult = Array.isArray(result) ? result[0] : result;

      if (payload.data.id && !payload?.needUpdate) {
        ctx.commit('set', { ...current, ...formattedResult });
      }

      return formattedResult;
    },

    async saveV2(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IModifyPayload<number, IBuildings & { utilities?: IBuildingUtilities[] }, { skipRefetch?: boolean }>,
    ) {
      try {
        if (ctx.rootState.language === 'en') {
          if (payload.data.ownershipGroups) {
            let ownershipGroup = payload.data.ownershipGroups[0];

            if (ownershipGroup && !ownershipGroup?.id) {
              ownershipGroup = await properties.processOwnershipGroup(ownershipGroup);
              const { settings } = ctx.getters;
              settings.ownershipGroups.push(ownershipGroup);
              ctx.commit('setSettings', { data: settings, meta: null });
            }

            await properties.saveRelation(
              'post',
              payload.data.id!,
              'ownership-groups',
              ownershipGroup ? [ownershipGroup.id] : [],
            );
          }

          if (payload.data.housingTypes) {
            const housingTypeIds = Array.from(payload.data.housingTypes, (h) => h.id);
            await properties.saveRelation('post', payload.data.id!, 'housing-types', housingTypeIds);
          }

          if (payload.data.ownershipTypes) {
            const ownershipTypeIds = Array.from(payload.data.ownershipTypes, (o) => o.id);
            await properties.saveRelation('post', payload.data.id!, 'ownership-types', ownershipTypeIds);
          }

          if (payload.data.utilities) {
            const utilityIds = Array.from(payload.data.utilities, (u) => u.id);
            await properties.saveRelation('post', payload.data.id!, 'utilities', utilityIds);
          }
        }

        const subset = subsetObject(payload.data, [
          'amenities',
          'buildingsHasPhotos',
          'city',
          'housingTypes',
          'listingFloorplans',
          'listingPhotos',
          'ownershipTypes',
          'region',
          'suiteTypes',
          'utilities',
          'videos',
          'virtualTours',
          'ownershipGroups',
        ]);

        const result = await properties.save(subset);

        if (payload.data.id && ctx.rootState.language === 'en' && !payload.additional?.skipRefetch) {
          await ctx.dispatch('get', { id: payload.data.id });
          ctx.commit('setUnits', ctx.state.units);
        }

        return Array.isArray(result) ? result[0] : result;
      } catch (e) {
        return null;
      }
    },

    async saveAmenities(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IAmenities[]>,
    ) {
      const current = ctx.getters.single;
      const ids = Array.from(payload.data, (a) => a.id);

      const result: IApiResponse<
      IBuildings2Amenities,
      IJunctionPostMetaResponse<IAmenities[]>
      > = await properties.saveRelation('post', payload.propertyId, 'amenities', ids);

      current.amenities = result.meta!.inverseRecords;
      ctx.commit('set', current);
    },

    async saveUnits(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<ISuiteTypes[]>,
    ) {
      const result: IApiResponse<ISuiteTypes[], null> = await properties.saveRelation(
        'put',
        payload.propertyId,
        'units',
        payload.data,
      );

      if (ctx.getters.single) {
        const current = ctx.getters.single;
        if (result.data) {
          result.data.forEach((u) => {
            const existingUnitIdx = current.suiteTypes.findIndex((cur: ISuiteTypes) => cur.id === u.id);

            if (existingUnitIdx !== -1) {
              current.suiteTypes[existingUnitIdx] = { ...current.suiteTypes[existingUnitIdx], ...u };
            }
          });
        }

        ctx.commit('set', current);
      } else {
        const current = { ...(ctx.state.unitModels || ctx.state.units) };

        if (result.data) {
          result.data.forEach((u) => {
            if (current.data) {
              const existingUnitIdx = current.data.findIndex((cur: ISuiteTypes) => cur.id === u.id);
              if (existingUnitIdx !== -1) {
                current.data[existingUnitIdx] = { ...current.data[existingUnitIdx], ...u };
              }
            }
          });

          ctx.commit((ctx.state.unitModels ? 'setUnitModels' : 'setUnits'), current);
        }
      }
    },

    async saveVideo(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildInsertPayload<IVideos>,
    ) {
      const operation = payload.data.id ? 'put' : 'post';
      const result = await properties.saveRelation(operation, payload.propertyId, 'videos', [payload.data]);
      const current = ctx.getters.single;

      if (operation === 'put') {
        result.data!.forEach((video: IVideos) => {
          const idx = current.videos.findIndex((v: IVideos) => v.id === video.id);
          current.videos[idx] = { ...current.videos[idx], ...video };
        });
      } else {
        current.videos = [...current.videos || [], ...result.data as IGenericObject[]];
      }

      ctx.commit('set', current);
    },

    async saveVirtualTour(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildInsertPayload<IVirtualTours>,
    ) {
      const operation = payload.data.id ? 'put' : 'post';
      const result = await properties.saveRelation(operation, payload.propertyId, 'virtual-tours', [payload.data]);
      const current = ctx.getters.single;

      if (operation === 'put') {
        result.data!.forEach((tour: IVirtualTours) => {
          const idx = current.virtualTours.findIndex((t: IVirtualTours) => t.id === tour.id);
          current.virtualTours[idx] = { ...current.virtualTours[idx], ...tour };
        });
      } else {
        current.virtualTours = [...current.virtualTours || [], ...result.data as IGenericObject[]];
      }

      ctx.commit('set', current);
    },

    async saveUtilities(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IBuildingUtilities[]>,
    ) {
      const current = ctx.getters.single;
      const ids = Array.from(payload.data, (u) => u.id);

      const result: IApiResponse<
      IBuildings2Utilities[],
      IJunctionPostMetaResponse<IBuildingUtilities[]>
      > = await properties.saveRelation('post', payload.propertyId, 'utilities', ids);

      if (payload?.forceUpdate) {
        current.utilities = [...result.meta!.inverseRecords];
        ctx.commit('set', current);
      }
    },

    async saveOwnershipTypes(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IOwnershipTypes[]>,
    ) {
      const current = ctx.getters.single;
      const ids = Array.from(payload.data, (o) => o.id);

      const result: IApiResponse<
      IBuildings2OwnershipTypes[],
      IJunctionPostMetaResponse<IOwnershipTypes[]>
      > = await properties.saveRelation('post', payload.propertyId, 'ownership-types', ids);

      if (payload?.forceUpdate) {
        current.ownershipTypes = [...result.meta!.inverseRecords];
        ctx.commit('set', current);
      }
    },

    async saveOwnershipGroups(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IOwnershipGroups[]>,
    ) {
      const current = ctx.getters.single;
      const ids = Array.from(payload.data, (o) => o.id);

      const result: IApiResponse<
      IOwnershipGroupHasBuildings[],
      IJunctionPostMetaResponse<IOwnershipTypes[]>
      > = await properties.saveRelation('post', payload.propertyId, 'ownership-groups', ids);

      if (payload?.forceUpdate) {
        current.ownershipGroups = [...result.meta!.inverseRecords];
        ctx.commit('set', current);
      }
    },

    async saveHousingTypes(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IHousingTypes[]>,
    ) {
      const current = ctx.getters.single;
      const ids = Array.from(payload.data, (h) => h.id);

      const result: IApiResponse<
      IBuildings2HousingTypes[],
      IJunctionPostMetaResponse<IHousingTypes[]>
      > = await properties.saveRelation('post', payload.propertyId, 'housing-types', ids);

      if (payload?.forceUpdate) {
        current.housingTypes = [...result.meta!.inverseRecords];
        ctx.commit('set', current);
      }
    },

    async saveUploadData(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyUploadResultPayload<IListingPhotos[] | IListingFloorplans[]>,
    ) {
      const result = await properties.saveRelation('put', payload.propertyId, payload.type, payload.data);
      let current = payload.type === 'listing-photos' ? ctx.state.photos : ctx.state.floorplans;

      if (result.data && !!current?.data) {
        result.data.forEach((item: Record<string, unknown>) => {
          if (payload.type === 'listing-photos' && 'available' in (current?.data ?? { data: [] })) {
            const photos = current as IPropertyStoreState['photos'];
            if (photos?.data) {
              const idx = photos.data?.available.findIndex((a: IListingPhotos) => a.id === item.id);

              if (idx !== -1) {
                photos.data.available[idx] = { ...photos.data.available[idx], ...item };
              }
            }

            current = photos;
          } else {
            const floorplans = current as IPropertyStoreState['floorplans'];

            if (floorplans?.data) {
              const idx = floorplans.data.findIndex((d) => d.id === item.id);

              if (idx !== -1) {
                floorplans.data[idx] = { ...floorplans.data[idx], ...item };
              }

              const property: IBuildings = ctx.getters.single;

              if (property && property.listingFloorplans) {
                const propertyIdx = property.listingFloorplans.findIndex((d) => d.id === item.id);

                if (propertyIdx !== -1) {
                  property.listingFloorplans[propertyIdx] = { ...property.listingFloorplans[idx], ...item };
                }

                ctx.commit('set', property);
              }
            }

            current = floorplans;
          }
        });
      }

      ctx.commit(payload.type === 'listing-photos' ? 'setPhotos' : 'setFloorplans', current);
    },

    async saveUploadResult(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyInsertUploadResultPayload,
    ) {
      const result = await properties.saveUploadedFileResults(
        payload.type,
        payload.propertyId,
        payload.clientId,
        payload.imgResults,
      );

      if (payload.type === 'listing-photos') {
        const current = ctx.state.photos;

        if (current?.data?.available) {
          current.data.available.unshift(...result.data as IListingPhotos[]);
        }

        ctx.commit('setPhotos', current);
      } else {
        const current = ctx.state.floorplans;
        const currentProperty = ctx.getters.single;

        if (current?.data) {
          current.data = [...current.data, ...result.data as IListingFloorplans[]];
          ctx.commit('setFloorplans', current);
        }

        if (currentProperty) {
          currentProperty.listingFloorplans = [
            ...currentProperty.listingFloorplans,
            ...result.data as IGenericObject[],
          ];
          ctx.commit('set', currentProperty);
        }
      }
    },

    async modifyAssignedPhotos(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyAssignedPhotoPayload<IListingPhotos[]>,
    ) {
      const ids: number[] = Array.from(payload.data, (v: IListingPhotos) => v.id!);

      let result: IApiResponse<Record<string, unknown>[], IMetaResponse & { inverseData: Record<string, unknown[]> }> | null;

      const current = ctx.getters.single;

      if (payload.operation === 'put') {
        result = await properties.saveRelation(
          payload.operation,
          payload.propertyId,
          'photos',
          ids,
          `extraFields=orderBy:${((current?.buildingsHasPhotos?.length || 0) + 1)}`,
        );
      } else {
        result = await properties.deleteRelation(payload.propertyId, 'photos', ids);
      }

      if (current) {
        if (payload.operation === 'put' && result?.data) {
          current.buildingsHasPhotos = [
            ...current.buildingsHasPhotos,
            ...(result.data.map((v, idx) => ({
              ...v, ...result?.meta?.inverseData && { listingPhoto: result.meta.inverseData[idx] },
            }))),
          ];
        } else {
          current.buildingsHasPhotos = current.buildingsHasPhotos.filter(
            (v: IBuildingsHasPhotos) => result?.data?.some(
              (d) => v.listingPhotoId !== d.listingPhotoId,
            ),
          );
        }

        ctx.commit('set', current);
      }
    },

    async modifyAssignedPhotoOrder(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IBuildingsHasPhotos[]>,
    ) {
      const result = await properties.saveRelation('put', payload.propertyId, 'property-has-photos', payload.data);
      const current = ctx.getters.single;
      current.buildingsHasPhotos = (payload.data.map((item: IBuildingsHasPhotos) => ({
        ...item,
        ...(result.data!.find((v: IBuildingsHasPhotos) => v.id === item.id)),
      })));
      ctx.commit('set', current);
    },

    async modifyFloorplanOrder(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IListingFloorplans[]>,
    ) {
      const result = await properties.saveRelation('put', payload.propertyId, 'floorplans', payload.data);
      const current = ctx.getters.single;
      current.listingFloorplans = (payload.data.map((item: IListingFloorplans) => ({
        ...item,
        ...(result.data!.find((v: IListingFloorplans) => v.id === item.id)),
      })));
      ctx.commit('set', current);
    },

    async modifyVideoOrder(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IVideos[]>,
    ) {
      const result = await properties.saveRelation('put', payload.propertyId, 'videos', payload.data);
      const current = ctx.getters.single;
      current.videos = (payload.data.map((item: IVideos) => ({
        ...item,
        ...(result.data!.find((v: IVideos) => v.id === item.id)),
      })));
      ctx.commit('set', current);
    },

    async modifyVirtualTourOrder(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IVirtualTours[]>,
    ) {
      const result = await properties.saveRelation('put', payload.propertyId, 'virtual-tours', payload.data);
      const current = ctx.getters.single;
      current.virtualTours = (payload.data.map((item: IVirtualTours) => ({
        ...item,
        ...(result.data!.find((v: IVirtualTours) => v.id === item.id)),
      })));
      ctx.commit('set', current);
    },

    async removeVideo(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IVideos>,
    ) {
      await properties.deleteRelation(payload.propertyId, 'videos', [payload.data.id!]);
      const current: IBuildings = ctx.getters.single;
      current.videos = current.videos?.filter((v) => v.id !== payload.data.id);
      ctx.commit('set', current);
    },

    async removeVirtualTour(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IVirtualTours>,
    ) {
      await properties.deleteRelation(payload.propertyId, 'virtual-tours', [payload.data.id!]);
      const current: IBuildings = ctx.getters.single;
      current.virtualTours = current.virtualTours?.filter((v) => v.id !== payload.data.id);
      ctx.commit('set', current);
    },

    async removeFloorplan(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IListingFloorplans>,
    ) {
      await properties.deleteRelation(payload.propertyId, 'floorplans', [payload.data.id!]);
      const current: IBuildings = ctx.getters.single;
      current.listingFloorplans = current.listingFloorplans?.filter((v) => v.id !== payload.data.id);
      ctx.commit('set', current);
      ctx.commit('setFloorplans', { data: current.listingFloorplans, meta: null });
    },

    async removeListingPhoto(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: IPropertyChildModifyPayload<IListingPhotos>,
    ) {
      await properties.deleteRelation(payload.propertyId, 'listing-photos', [payload.data.id!]);
      const current = { ...ctx.state.photos };
      const currentProperty: IBuildings = ctx.getters.single;

      current!.data!.assigned = current!.data!.assigned.filter(
        (v: IListingPhotos) => v.id !== payload.data.id,
      );

      current!.data!.available = current!.data!.available.filter(
        (v: IListingPhotos) => v.id !== payload.data.id,
      );

      ctx.commit('setPhotos', current);

      if (currentProperty) {
        currentProperty.buildingsHasPhotos = currentProperty.buildingsHasPhotos?.filter(
          (v) => v.listingPhotoId !== payload.data.id,
        );

        ctx.commit('set', currentProperty);
      }
    },

    async remove(ctx: ActionContext<IPropertyStoreState, typeof store.state>, propertyId: number) {
      await properties.remove([propertyId]);
    },

    async getCSV(
      ctx: ActionContext<IPropertyStoreState, typeof store.state>,
      payload: Omit<IExportFilterPayload, 'filters'> & { filters: ISearchCommandOptions },
    ) {
      // Strip aggregations and their backed select fields — they drive display-only table columns and are not needed for CSV output.
      // Including them causes one correlated subquery per row per aggregation, which causes a bottleneck and even times out on large property sets.
      const { aggregations: _agg, select: _sel, ...exportFilters } = payload.filters;
      const result: IApiResponse<Partial<IBuildings>, IMetaResponse> = await adminRequest.post(
        '/v1/property-api/properties/_search?csvExport=SAME_METHOD',
        {
          ...exportFilters,
          limit: ctx.state.list?.meta?.totalRecords ?? 999,
          page: 1,
          fields: payload.fields,
        },
      );

      downloadCSV(result as string, `${dayjs().format('YYYY-MM-DD')}_property_list.csv`);
    },
  },
};
